import React from 'react';
import { Link } from 'react-router-dom';
import { Mail, MessageCircle, CreditCard, Smartphone } from 'lucide-react';
import { PublicPageChrome } from './PublicPageChrome';
import { PIXLIE_SUPPORT_EMAIL, pixlieSupportMailto } from '../../constants/support';

const SupportPage: React.FC = () => (
  <PublicPageChrome>
    <div className="mx-auto w-full max-w-4xl">
      <div className="mb-12 text-center">
        <span className="mb-4 block font-mono text-xs uppercase tracking-[0.2em] text-secondary">
          Pixlie Support
        </span>
        <h1 className="mb-8 bg-gradient-to-b from-white to-white/20 bg-clip-text text-5xl font-black tracking-tighter text-transparent md:text-7xl">
          Contact support
        </h1>
        <div className="mx-auto mb-8 h-1 w-24 bg-gradient-to-r from-transparent via-secondary to-transparent" />
        <p className="mx-auto max-w-2xl text-lg font-light leading-relaxed text-white/50">
          We help with account access, billing, generation jobs, and the Pixlie iOS app. English support by email.
        </p>
      </div>

      <div className="mb-10 rounded-2xl border border-secondary/30 bg-secondary/10 p-8 text-center">
        <p className="mb-2 text-[10px] font-bold uppercase tracking-[0.28em] text-secondary">Primary contact</p>
        <a
          href={pixlieSupportMailto()}
          className="text-2xl font-semibold text-white transition-colors hover:text-secondary md:text-3xl"
        >
          {PIXLIE_SUPPORT_EMAIL}
        </a>
        <p className="mt-4 text-sm text-white/45">
          Include your account email and, if relevant, a job ID from the dashboard.
        </p>
        <a
          href={pixlieSupportMailto()}
          className="mt-6 inline-flex items-center gap-2 rounded-xl bg-white px-6 py-3 text-xs font-bold uppercase tracking-widest text-black transition-colors hover:bg-secondary"
        >
          <Mail className="h-4 w-4" />
          Email support
        </a>
      </div>

      <div className="grid gap-6 md:grid-cols-3">
        <div className="rounded-xl border border-white/10 bg-white/5 p-6">
          <MessageCircle className="mb-3 h-6 w-6 text-secondary" />
          <h2 className="mb-2 text-lg font-semibold text-white">Generation &amp; queue</h2>
          <p className="text-sm leading-relaxed text-white/60">
            Stuck jobs, failed renders, or credit balance questions. Send the workflow type (t2v, i2i, etc.) and
            approximate time of the attempt.
          </p>
        </div>
        <div className="rounded-xl border border-white/10 bg-white/5 p-6">
          <CreditCard className="mb-3 h-6 w-6 text-secondary" />
          <h2 className="mb-2 text-lg font-semibold text-white">Billing &amp; plans</h2>
          <p className="text-sm leading-relaxed text-white/60">
            Stripe checkout and subscription changes happen on{' '}
            <Link to="/billing" className="text-secondary underline hover:no-underline">
              pixlys.com/billing
            </Link>
            . For invoice or portal issues, email us with your billing email.
          </p>
        </div>
        <div className="rounded-xl border border-white/10 bg-white/5 p-6">
          <Smartphone className="mb-3 h-6 w-6 text-secondary" />
          <h2 className="mb-2 text-lg font-semibold text-white">iOS app</h2>
          <p className="text-sm leading-relaxed text-white/60">
            Sign-in, email verification, and promo codes in the app (Settings → Credits &amp; promo). Same inbox for
            TestFlight and App Store builds.
          </p>
        </div>
      </div>

      <div className="mt-10 space-y-4 text-sm leading-relaxed text-white/55">
        <p>
          <strong className="text-white">Response time:</strong> we aim to reply within 1–2 business days. Urgent
          production outages get priority when tagged in the subject line.
        </p>
        <p>
          <strong className="text-white">Operator:</strong> Brightforge Software Inc., Delaware C-Corporation.{' '}
          <a href="https://brightforge.ltd/" target="_blank" rel="noopener noreferrer" className="text-secondary hover:underline">
            brightforge.ltd
          </a>
        </p>
        <p>
          Legal: <a href="/privacy/" className="text-secondary hover:underline">Privacy Policy</a>
          {' · '}
          <a href="/terms/" className="text-secondary hover:underline">Terms of Service</a>
        </p>
      </div>
    </div>
  </PublicPageChrome>
);

export default SupportPage;
